/* Journal Booking — printed quarterly spread.
   Reservation as a magazine sequence. Roman numerals + chapter eyebrows +
   running heads + paper texture, matching the Journal template's home/portfolio
   typography. Two-column spread: caption + form on the left, ledger card on
   the right. */

import { useEffect, useRef } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useBookingFlow, startingPrice, formatPrice } from "@/hooks/useBookingFlow";
import BookingTermsAcceptance from "@/components/BookingTermsAcceptance";

const ROMAN = ["I", "II", "III", "IV"];

const CHAPTERS = {
  1: { label: "Selecting your sitting", caption: "Every entry begins with a choice. Below: this season's commissions, kept simple." },
  2: { label: "Setting the date",       caption: "We hold the morning, you bring the light. Pick a day and we'll write back to confirm." },
  3: { label: "A note to the editor",   caption: "Tell us who's coming and a sentence about what you'd like remembered." },
  4: { label: "The ledger",             caption: "The full entry as it will be filed. Confirm to send." },
};

export default function JournalBooking() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const f = useBookingFlow({ totalSteps: 4 });

  useEffect(() => { document.title = `Reservation · ${studioName}`; }, [studioName]);

  const chapter = CHAPTERS[f.step];
  const numeral = ROMAN[f.step - 1];

  // Smooth-scroll the new chapter into view on every step transition so
  // mobile readers don't have to scroll up from the ledger / action bar
  // to find the next chapter's body. See Editorial Booking for full rationale.
  const chapterRef = useRef(null);
  const firstChapterMount = useRef(true);
  useEffect(() => {
    if (firstChapterMount.current) {
      firstChapterMount.current = false;
      return;
    }
    const el = chapterRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [f.step]);

  function nextOrSubmit() {
    if (f.step < 4) f.setStep(f.step + 1);
    else f.submit();
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F2EC", color: "#0E0D0C", fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }} data-testid="journal-booking">
      {/* Paper texture */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]" aria-hidden
        style={{
          backgroundImage: "radial-gradient(circle at 25% 35%, #000 0.5px, transparent 0.7px), radial-gradient(circle at 80% 65%, #000 0.5px, transparent 0.7px)",
          backgroundSize: "4px 4px, 7px 7px",
        }}/>
      {/* Running head */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-14">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)]">
          <span>{studioName} · Reservation</span>
          <span>Chapter {numeral} · {chapter.label}</span>
        </div>
        <div className="mt-2 h-px bg-[color:var(--tenant-accent,#C9A961)]/40" />
      </div>

      {/* Spread */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 mt-16 pb-32 grid lg:grid-cols-[1fr_320px] gap-14 lg:gap-20">
        <div ref={chapterRef} className="scroll-mt-24">
          <div className="flex items-baseline gap-5 mb-6">
            <span className="font-light italic leading-none" style={{ fontSize: "clamp(4rem, 9vw, 7rem)" }}>{numeral}</span>
            <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-fg,#0E0D0C)]/55">Chapter {numeral} — Step</div>
          </div>
          <h1 className="font-light italic leading-[1.0] tracking-[-0.01em]" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
            {chapter.label}.
          </h1>
          <p className="mt-5 text-base text-[color:var(--tenant-fg,#0E0D0C)]/65 max-w-xl" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
            {chapter.caption}
          </p>

          {/* Body */}
          <div className="mt-12">
            {f.step === 1 && (
              <ul className="divide-y divide-[color:var(--tenant-fg,#0E0D0C)]/12" data-testid="journal-step-package">
                {f.services.map((s, i) => {
                  const sel = (s.id === f.selected?.id) || (s.slug === f.selected?.slug);
                  const price = startingPrice(s);
                  return (
                    <li key={s.id || i}>
                      <button type="button" onClick={() => f.setSelectedId(s.id || s.slug)} data-testid={`journal-package-${i}`}
                        className="group w-full text-left py-6 flex items-baseline justify-between gap-5">
                        <div className="flex items-baseline gap-5">
                          <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)] w-8 shrink-0">{ROMAN[i] || ""}</span>
                          <div>
                            <div className="font-light text-3xl tracking-tight">{s.name}</div>
                            <div className="text-xs text-[color:var(--tenant-fg,#0E0D0C)]/55 mt-1.5 max-w-md" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{s.description}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="italic text-xl">{formatPrice(price) || "by request"}</div>
                          <div className="text-[10px] uppercase tracking-[0.32em] mt-1 text-[color:var(--tenant-fg,#0E0D0C)]/55" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{sel ? "✓ filed" : "file →"}</div>
                        </div>
                      </button>
                      {/* Inline Edition picker — opens directly under the chosen
                          sitting so the reader sees the tiers as a natural
                          continuation of the entry, not a separate appendix. */}
                      {sel && s.tiers?.length > 0 && (
                        <div className="ml-13 pl-13 pb-7 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="journal-tier-block" style={{ marginLeft: "3.25rem" }}>
                          <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)] mb-4" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>Edition</div>
                          <ul className="space-y-2">
                            {s.tiers.map((t) => {
                              const active = t.name === f.tierName;
                              return (
                                <li key={t.name}>
                                  <button type="button" onClick={() => f.setTierName(t.name)} data-testid={`journal-tier-${t.name}`}
                                    className={`w-full text-left flex items-baseline justify-between gap-4 py-2 px-3 -mx-3 transition-colors ${active ? "bg-[color:var(--tenant-accent-soft,rgba(201,169,97,0.10))]" : "hover:bg-[#0E0D0C]/[0.04]"}`}>
                                    <div>
                                      <span className="italic text-lg">{t.name}</span>
                                      {t.description && <span className="text-xs text-[color:var(--tenant-fg,#0E0D0C)]/55 ml-3" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{t.description}</span>}
                                    </div>
                                    <span className="italic text-base">{formatPrice(Number(t.price))}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* (legacy tier block was moved inline above. nothing here.) */}

            {f.step === 2 && (
              <div className="space-y-7 max-w-md" data-testid="journal-step-date">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)]" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>Date</span>
                  <input type="date" value={f.date} onChange={(e) => f.setDate(e.target.value)} data-testid="journal-date"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/25 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-2 outline-none italic text-2xl" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)]" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>Hour</span>
                  <select value={f.time} onChange={(e) => f.setTime(e.target.value)} data-testid="journal-time"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/25 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-2 outline-none italic text-2xl">
                    {["09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
            )}

            {f.step === 3 && (
              <div className="space-y-7 max-w-md" data-testid="journal-step-details">
                {[
                  { k: "name",  l: "Author",            t: "text",  ph: "Jane Doe" },
                  { k: "email", l: "Correspondence",    t: "email", ph: "jane@studio.com" },
                  { k: "phone", l: "Telephone (optional)", t: "tel", ph: "+1 555 0100" },
                ].map((field) => (
                  <label key={field.k} className="block">
                    <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)]" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{field.l}</span>
                    <input type={field.t} value={f.details[field.k]} placeholder={field.ph} data-testid={`journal-${field.k}`}
                      onChange={(e) => f.setDetails({ ...f.details, [field.k]: e.target.value })}
                      className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/25 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-2 outline-none italic text-xl placeholder:text-[color:var(--tenant-fg,#0E0D0C)]/30" />
                  </label>
                ))}
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)]" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>A short note</span>
                  <textarea rows={4} value={f.details.notes} placeholder="A sentence on the entry to come."
                    onChange={(e) => f.setDetails({ ...f.details, notes: e.target.value })}
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/25 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-2 outline-none italic text-lg placeholder:text-[color:var(--tenant-fg,#0E0D0C)]/30 resize-none" data-testid="journal-notes" />
                </label>
              </div>
            )}

            {f.step === 4 && (
              <div className="max-w-md text-base space-y-3" data-testid="journal-step-review" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                {[
                  ["Sitting", f.selected?.name],
                  ["Edition", f.selectedTier?.name],
                  ["Date",    f.date || "—"],
                  ["Hour",    f.time],
                  ["Author",  f.details.name || "—"],
                  ["Total",   formatPrice(Number(f.selectedTier?.price)) || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between border-b border-[color:var(--tenant-fg,#0E0D0C)]/12 pb-2">
                    <span className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-fg,#0E0D0C)]/55">{k}</span>
                    <span className="italic text-xl" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{v}</span>
                  </div>
                ))}
                <div className="pt-5">
                  <BookingTermsAcceptance
                    policyText={f.policyText}
                    checked={f.termsAccepted}
                    onChange={f.setTermsAccepted}
                    testidPrefix="journal-booking-terms"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-14 flex items-center gap-8" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
            <button type="button" onClick={f.goBack} disabled={f.step === 1} data-testid="journal-booking-back"
              className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55 hover:text-[color:var(--tenant-fg,#0E0D0C)] disabled:opacity-30 transition-colors">
              ← Previous chapter
            </button>
            <button type="button" onClick={nextOrSubmit} disabled={!f.canForward || f.submitting} data-testid="journal-booking-next"
              className="px-6 py-2 border text-[10px] uppercase tracking-[0.38em] text-[color:var(--tenant-fg,#0E0D0C)] disabled:opacity-30 transition-colors hover:bg-[#0E0D0C]/5"
              style={{ borderColor: "var(--tenant-accent, #C9A961)" }}>
              {f.submitting ? "Filing…" : f.step < 4 ? "Next chapter →" : "File reservation →"}
            </button>
          </div>
        </div>

        {/* Right rail — ledger card */}
        <aside className="lg:sticky lg:top-12 self-start" style={{ fontFamily: "var(--tenant-sans, system-ui)" }} data-testid="journal-ledger">
          <div className="border border-[color:var(--tenant-accent,#C9A961)]/40 bg-white/40 backdrop-blur-sm p-7">
            <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#C9A961)] mb-5">The Ledger</div>
            <dl className="space-y-4 text-sm">
              {[
                ["Sitting", f.selected?.name || "—"],
                ["Edition", f.selectedTier?.name || "—"],
                ["Date",    f.date || "—"],
                ["Hour",    f.time || "—"],
                ["Total",   formatPrice(Number(f.selectedTier?.price)) || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 border-b border-[color:var(--tenant-fg,#0E0D0C)]/10 pb-2">
                  <dt className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">{k}</dt>
                  <dd className="italic text-base text-right" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}
